import {
  ArchetypeDiscovery,
  DimensionOutroSummary,
  ElementDiscovery,
  PatternDiscovery,
} from "@dimensional-engineering/dimensional-models";

import styles from "./DiscoveredElements.module.css";

import Archetype from "@/components/traits/arcehtype/Archetypes/Archetype";
import Element from "@/components/traits/element/Element/Element";
import Pattern from "@/components/traits/pattern/Pattern/Pattern";
import jsonElements from "../../../../../assets/traitInfo/elements.json";
import jsonPatterns from "../../../../../assets/traitInfo/patterns.json";
import {
  getUserPatternScore,
  getUserScaleScore,
} from "@/components/traits/utils";
import { useModalContext } from "@/context/ModalContext";
import ElementModal from "@/components/traits/TraitsModals/Element/ElementModal";
import PatternModal from "@/components/traits/TraitsModals/Pattern/PatternModal";
import ArchetypeModal from "@/components/traits/TraitsModals/Archetype/ArchetypeModal";
import { useStaggeredOpacity } from "@/helpers/useStaggeredOpacity";

type Props = {
  outro: DimensionOutroSummary | undefined;
};

export default function DiscoveredElements(props: Props) {
  const { setModalComponent } = useModalContext();

  const opacity = useStaggeredOpacity(4);

  const mapped = [
    props.outro?.elementDiscoveries.sort((a, b) => {
      if (a.scaleScore?.scaleName && b.scaleScore?.scaleName) {
        return a.scaleScore.scaleName?.localeCompare(b.scaleScore?.scaleName);
      } else {
        return 0;
      }
    }),
    props.outro?.archetypeDiscoveries.sort((a, b) => {
      return a.archetype.name.localeCompare(b.archetype.name);
    }),
    props.outro?.patternDiscoveries.sort((a, b) => {
      const patternA = jsonPatterns?.find((p) => p.slug === a.patternSlug);
      const patternB = jsonPatterns?.find((p) => p.slug === b.patternSlug);
      if (patternA && patternB) {
        return patternA.name.localeCompare(patternB.name);
      }
      return 0;
    }),
  ]
    .flat()
    .map((el, index) => {
      if (el?.discoveryType === "element") {
        const element = el as ElementDiscovery;
        let color = "gray";
        let name;
        jsonElements.forEach((json) => {
          if (json.slug === element.scaleSlug) {
            name = json.alias ?? json.name;
          }
        });
        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setModalComponent(<ElementModal slug={element.scaleSlug} />);
            }}
            style={{ cursor: "pointer" }}
            key={element.scaleSlug}
            className="elBodyNoMarginMobile"
          >
            <Element
              maxWidth
              slug={element.scaleSlug}
              score={getUserScaleScore(
                element.scaleScore?.percentScore,
                element.scaleSlug,
                element.scaleScore?.dimensionSlugSource
              )}
            />
          </div>
        );
      } else if (el?.discoveryType === "pattern") {
        const pattern = el as PatternDiscovery;
        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setModalComponent(<PatternModal slug={pattern.patternSlug} />);
            }}
            style={{ cursor: "pointer" }}
            key={pattern.patternSlug}
            className="elBodyNoMarginMobile"
          >
            <Pattern
              maxWidth
              patternSlug={pattern.patternSlug}
              score={getUserPatternScore(
                pattern.percentScore,
                pattern.patternSlug,
                pattern.isLongFormGenerated
              )}
            />
          </div>
        );
      } else {
        const archetype = el as ArchetypeDiscovery;
        if (archetype) {
          return (
            <div
              onClick={(e) => {
                e.stopPropagation();
                setModalComponent(
                  <ArchetypeModal slug={archetype.archetypeSlug} />
                );
              }}
              style={{ cursor: "pointer" }}
              key={archetype.archetypeSlug}
              className="elBodyNoMarginMobile"
            >
              <Archetype
                maxWidth
                archetype={archetype.archetype}
                score={archetype.archetypeScore}
              />
            </div>
          );
        }
      }
    });

  return (
    <div className={styles.main}>
      <p
        style={{ opacity: opacity.step1 }}
        className={styles.discoveredTraitsText}
      >
        DISCOVERD TRAITS
      </p>
      <p style={{ opacity: opacity.step2 }} className={styles.traitCount}>
        {mapped.length}
      </p>
      <p style={{ opacity: opacity.step3 }} className={styles.traitCountText}>
        You unlocked {mapped.length} trait scores on this Dimension
      </p>
      <div style={{ opacity: opacity.step4 }} className={styles.parent}>
        <div style={{ opacity: opacity.step4 }} className={styles.wrapper}>
          {mapped}
        </div>
      </div>
    </div>
  );
}
